'use client';

import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "@/i18n/client";
import { useLocale } from "@/i18n/context";
import type { BlogEntry } from "@/content/blog";

interface Props {
  entry: BlogEntry;
  related: BlogEntry[];
}

export function BlogPostContent({ entry, related }: Props) {
  const { t, isLoading } = useTranslations();
  const { locale } = useLocale();
  const bp = t('blog').post;

  if (isLoading) return null;

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  return (
    <>
      {/* BACK LINK */}
      <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-7">
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.16em] uppercase text-[color:var(--color-mute)] no-underline hover:text-[color:var(--color-accent)] transition-colors"
          aria-label={bp.back}
        >
          {bp.back}
        </Link>
      </div>

      {/* ARTICLE HEADER */}
      <header className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-10 pb-14">
        <div className="max-w-[720px] mx-auto">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[color:var(--color-accent)] mb-5">
            {entry.category}
          </div>
          <h1 className="font-sans font-bold tracking-tight text-[40px] md:text-[52px] leading-[1.05] text-[color:var(--color-ink)] m-0">
            {entry.title}
          </h1>
          <time
            dateTime={entry.date}
            className="block mt-5 font-mono text-[11px] tracking-[0.16em] uppercase text-[color:var(--color-mute)]"
          >
            {formatDate(entry.date)}
          </time>
        </div>
        {entry.image && (
          <div className="mt-14 max-w-[960px] mx-auto relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-lg overflow-hidden border border-[color:var(--color-border)]">
            <Image
              src={entry.image}
              alt={entry.title}
              fill
              className="object-cover"
              sizes="100vw"
              priority
              unoptimized
            />
          </div>
        )}
      </header>

      {/* ARTICLE BODY */}
      <article className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-24">
        <div className="max-w-[720px] mx-auto">
          <div className="border-t border-[color:var(--color-border)] pt-10 blog-prose">
            {entry.cuerpo.map((paragraph, i) => (
              <p
                key={i}
                className={`font-sans text-[17px] leading-[1.75] text-[color:var(--color-ink)] mb-6 ${
                  i === 0 ? "first-paragraph" : ""
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      {/* RELATED POSTS */}
      {related.length > 0 && (
        <section
          className="bg-[color:var(--color-bg)] py-24"
          aria-label={bp.related}
        >
          <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14">
            <div className="grid grid-cols-[80px_1fr] gap-6 mb-10">
              <div />
              <h2 className="font-sans font-[350] text-[36px] leading-[1.05] tracking-[-0.02em] text-[color:var(--color-ink)] m-0">
                {bp.related}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((rel) => (
                <article
                  key={rel.slug}
                  className="group bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-md overflow-hidden hover:border-[color:var(--color-accent)] transition-colors"
                >
                  <Link
                    href={`/blog/${rel.slug}`}
                    className="block no-underline flex flex-col h-full"
                    aria-label={`${bp.readMore} ${rel.title}`}
                  >
                    {rel.image && (
                      <div className="relative w-full h-[200px] border-b border-[color:var(--color-border)] overflow-hidden">
                        <Image
                          src={rel.image}
                          alt={rel.title}
                          fill
                          className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          unoptimized
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--color-accent)] mb-3">
                      {rel.category}
                    </div>
                    <h3 className="font-sans font-[350] text-[22px] leading-[1.1] tracking-[-0.01em] text-[color:var(--color-ink)] m-0 mb-3 group-hover:text-[color:var(--color-accent)] transition-colors">
                      {rel.title}
                    </h3>
                    <p className="font-sans text-[13px] leading-[1.6] text-[color:var(--color-mute)] m-0">
                      {rel.excerpt.slice(0, 100)}…
                    </p>
                    <div className="mt-4 font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--color-accent)] border-b border-[color:var(--color-accent)] inline-block pb-0.5">
                      {bp.readMore}
                    </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
